import { useState } from "react";
import Layout from "@/components/feature/Layout";

const FORM_URL = "https://readdy.ai/api/form/db7ltr0mkl3spn8smae0";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "联系邮箱",
    value: "contact@chiguabaoliao.com",
    note: "工作日 24 小时内回复",
  },
  {
    icon: "ri-phone-line",
    label: "联系电话",
    value: "400-000-8888",
    note: "周一至周五 09:00 - 18:00",
  },
  {
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "示例市娱乐传媒产业园 A 座 18 层",
    note: "来访请提前预约",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      return;
    }

    setSubmitting(true);
    setFormError("");
    setStatus("idle");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch (err) {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <span className="inline-block px-3 py-1 rounded-md bg-background-50/15 text-background-50 text-xs font-medium mb-3">
            联系我们
          </span>
          <h1 className="text-3xl md:text-4xl font-black text-background-50">随时与我们取得联系</h1>
          <p className="mt-3 text-sm md:text-base text-background-100 max-w-2xl leading-relaxed">
            无论是提供新闻线索、内容合作，还是对网站功能的建议与反馈，都欢迎通过下方方式联系我们。
          </p>
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Contact info */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            {contactInfo.map((info) => (
              <div
                key={info.label}
                className="rounded-lg bg-background-50 border border-background-200 p-5 flex items-start gap-4"
              >
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 text-xl">
                  <i className={info.icon} />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-400">{info.label}</p>
                  <p className="mt-1 font-medium text-foreground-900 break-all">{info.value}</p>
                  <p className="mt-1 text-xs text-foreground-400">{info.note}</p>
                </div>
              </div>
            ))}
            <div className="rounded-lg bg-secondary-900 p-5 text-background-100">
              <h3 className="font-bold text-background-50 flex items-center gap-2">
                <i className="ri-information-line text-accent-400" />
                温馨提示
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                本站为前端演示站点，表单提交能力已接入，但页面内容与联系方式均为示范信息，请以实际发布为准。
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200 p-5 md:p-8">
            <h2 className="text-xl md:text-2xl font-black text-foreground-950">留言给我们</h2>
            <p className="mt-2 text-sm text-foreground-500">填写下方表单，我们会尽快查看你的留言。</p>

            {status === "success" ? (
              <div className="mt-6 rounded-lg bg-accent-50 border border-accent-200 p-6 flex items-start gap-4">
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-accent-500 text-secondary-950 text-2xl">
                  <i className="ri-check-line" />
                </span>
                <div>
                  <p className="font-bold text-foreground-900">留言已提交，感谢你的反馈！</p>
                  <p className="mt-1 text-sm text-foreground-600">
                    我们会尽快阅读你的留言。如需继续留言，可点击下方按钮。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-4 h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的姓名"
                      className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入你的邮箱"
                      className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-2">
                    留言主题
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    defaultValue="新闻线索"
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                  >
                    <option value="新闻线索">新闻线索 / 爆料</option>
                    <option value="内容合作">内容合作</option>
                    <option value="功能建议">功能建议</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请填写你的留言内容（最多 500 字）"
                    className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
                  />
                </div>

                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  className="hp-field-verify"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="text-sm text-primary-600 flex items-center gap-2">
                    <i className="ri-error-warning-line" />
                    {formError || "提交失败，请稍后重试"}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="h-11 px-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-send-plane-fill" />
                    {submitting ? "提交中…" : "提交留言"}
                  </button>
                  <p className="text-xs text-foreground-400">
                    提交即表示你同意我们根据隐私政策处理你的信息。
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}